import { useEffect, useState } from "react";
import { Loader2, PackagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const emptyProduct = { name: "", selling_price: "", unit_cost: "", current_quantity: "", low_stock_threshold: "" };

export const ProductModal = ({ open, onOpenChange, product, onSave }) => {
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(product ? {
      name: product.name,
      selling_price: product.selling_price,
      unit_cost: product.unit_cost,
      current_quantity: product.current_quantity,
      low_stock_threshold: product.low_stock_threshold,
    } : emptyProduct);
  }, [product, open]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const valid = form.name.trim() && ["selling_price", "unit_cost", "current_quantity", "low_stock_threshold"].every((key) => Number(form[key]) >= 0);
  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    const ok = await onSave(product?.id, {
      name: form.name.trim(),
      selling_price: Number(form.selling_price || 0),
      unit_cost: Number(form.unit_cost || 0),
      current_quantity: Number(form.current_quantity || 0),
      low_stock_threshold: Number(form.low_stock_threshold || 0),
    });
    setSaving(false);
    if (ok) onOpenChange(false);
  };
  const inputClass = "h-11 bg-[#111] border-white/10 text-white";

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="add-edit-product-modal" className="bg-[#0a0a0a] border-white/10 text-white max-w-lg">
      <DialogHeader><DialogTitle className="font-heading text-2xl">{product ? "Edit product" : "Add product"}</DialogTitle><DialogDescription className="text-zinc-500">Set unit economics and the stock level that should trigger an alert.</DialogDescription></DialogHeader>
      <div className="grid grid-cols-2 gap-4 py-3">
        <div className="col-span-2 space-y-1.5"><Label>Product name *</Label><Input data-testid="product-modal-input-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputClass} placeholder="Ceramic coating" /></div>
        <div className="space-y-1.5"><Label>Selling price</Label><Input data-testid="product-modal-input-selling-price" type="number" min="0" step="0.01" value={form.selling_price} onChange={(e) => set("selling_price", e.target.value)} className={inputClass} /></div>
        <div className="space-y-1.5"><Label>Unit cost</Label><Input data-testid="product-modal-input-cost-price" type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} className={inputClass} /></div>
        <div className="space-y-1.5"><Label>Current quantity</Label><Input data-testid="product-modal-input-quantity" type="number" min="0" step="0.01" value={form.current_quantity} onChange={(e) => set("current_quantity", e.target.value)} className={inputClass} /></div>
        <div className="space-y-1.5"><Label>Low-stock alert at</Label><Input data-testid="product-modal-input-threshold" type="number" min="0" step="0.01" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className={inputClass} /></div>
      </div>
      <DialogFooter><button data-testid="product-modal-cancel-btn" type="button" onClick={() => onOpenChange(false)} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-zinc-300 hover:bg-white/5 transition-colors">Cancel</button><button data-testid="product-modal-submit-btn" type="button" disabled={!valid || saving} onClick={submit} className="rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 hover:bg-zinc-200 transition-colors">{saving ? <Loader2 size={14} className="animate-spin" /> : <PackagePlus size={14} />} Save product</button></DialogFooter>
    </DialogContent>
  </Dialog>;
};
